/* ==========================================================================
   WADES — belt.js
   Loaded only on the belt product template. Everything here is scoped to
   [data-belt-root] or a belt section, so it can never fight theme.js.

   Cart writes go through window.WADESAddToCart (theme.js) so the belt page
   reuses the same drawer, free-shipping meter and add-to-cart upsell popup as
   the rest of the store instead of shipping a second cart implementation.
   ========================================================================== */
(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion:reduce)').matches;
  // Locale copy comes from theme.liquid, never from this file.
  var SAVE_FMT = ((window.WADES || {}).strings || {}).youSave || '%s';
  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  // theme.js formats with the shop's own money format and shares it here, so a
  // bundle total and a cart line can never disagree about how a price looks.
  // The alarm page prints whole amounts without ",00" (money_without_trailing_zeros
  // in its Liquid), so the totals written here match and nothing reformats on load.
  var wholeKr = document.body.classList.contains('alarm-page');
  function money(cents) {
    var s = window.WADESMoney ? window.WADESMoney(cents) : (cents / 100).toFixed(2).replace('.', ',') + ' kr';
    return wholeKr ? s.replace(/([.,])00(?=\D*$)/, '') : s;
  }

  /* ============================================================
     1. GALLERY — slides, thumbs, arrows, swipe, video
     ============================================================ */
  (function () {
    var gal = qs('[data-belt-gallery]');
    if (!gal) return;

    var slides = qsa('[data-belt-slide]', gal);
    var thumbs = qsa('[data-belt-thumb]', gal);
    var countEl = qs('[data-belt-count]', gal);
    var video = qs('[data-belt-video]', gal);
    if (!slides.length) return;

    var current = Math.max(0, slides.findIndex(function (s) { return s.classList.contains('is-active'); }));
    var moved = false; // flipped by the first render, so the initial one never scrolls

    // The thumbnail rail fades at whichever end still has more to show. Both
    // fades go once the rail is short enough to need no scrolling at all.
    var rail = qs('[data-belt-thumbs]', gal);
    var railWrap = qs('[data-belt-thumbwrap]', gal);
    function edges() {
      if (!rail || !railWrap) return;
      var slack = rail.scrollWidth - rail.clientWidth;
      railWrap.classList.toggle('is-start', rail.scrollLeft <= 1 || slack <= 1);
      railWrap.classList.toggle('is-end', rail.scrollLeft >= slack - 1);
    }
    if (rail) {
      rail.addEventListener('scroll', edges, { passive: true });
      window.addEventListener('resize', edges, { passive: true });
    }

    // Slides past the first two ship without a src so a page of colour shots
    // does not download all at once. Idempotent, so it can run on every move.
    function hydrate(slide) {
      var img = slide && slide.querySelector('img[data-src]');
      if (!img) return;
      var set = img.getAttribute('data-srcset');
      if (set) img.setAttribute('srcset', set);
      img.setAttribute('src', img.getAttribute('data-src'));
      img.removeAttribute('data-src');
      img.removeAttribute('data-srcset');
    }

    function show(i) {
      current = (i + slides.length) % slides.length;
      // This one and the next, so the following tap has nothing to wait for.
      hydrate(slides[current]);
      hydrate(slides[(current + 1) % slides.length]);
      slides.forEach(function (s, n) { s.classList.toggle('is-active', n === current); });
      thumbs.forEach(function (t) {
        t.classList.toggle('is-active', parseInt(t.getAttribute('data-belt-thumb'), 10) === current);
      });
      if (countEl) countEl.textContent = (current + 1) + ' / ' + slides.length;

      // The film only runs while it is the visible slide.
      if (video) {
        var onVideo = slides[current].hasAttribute('data-belt-video-slide');
        if (onVideo) playVideo(); else video.pause();
      }
      // Only chase the thumbnail once the visitor has actually moved the
      // gallery. Doing it on the first render scrolls the whole page down
      // before the images have laid out, so the page opens halfway in.
      // Scroll the rail by hand rather than with scrollIntoView. That method
      // moves every scrollable ancestor, so on a phone it dragged the whole
      // page down to the thumbnails and left the photo itself off screen.
      var active = thumbs[current];
      if (moved && active && rail) {
        var want = active.offsetLeft - (rail.clientWidth - active.offsetWidth) / 2;
        var max = rail.scrollWidth - rail.clientWidth;
        want = Math.max(0, Math.min(want, max));
        if (rail.scrollTo) rail.scrollTo({ left: want, behavior: reduced ? 'auto' : 'smooth' });
        else rail.scrollLeft = want;
      }
      moved = true;
      edges();
    }

    function playVideo() {
      if (!video) return;
      if (video.preload === 'none') { video.preload = 'auto'; video.load(); }
      var p = video.play();
      if (p && p.catch) p.catch(function () {});
    }

    gal.addEventListener('click', function (e) {
      if (e.target.closest('[data-belt-prev]')) { show(current - 1); return; }
      if (e.target.closest('[data-belt-next]')) { show(current + 1); return; }
      var th = e.target.closest('[data-belt-thumb]');
      if (th) show(parseInt(th.getAttribute('data-belt-thumb'), 10));
    });

    // Horizontal swipe. Vertical intent is left alone so the page still scrolls.
    var sx = 0, sy = 0, tracking = false;
    var stage = qs('.beltg__stage', gal);
    if (stage) {
      stage.addEventListener('touchstart', function (e) {
        if (e.touches.length !== 1) return;
        sx = e.touches[0].clientX; sy = e.touches[0].clientY; tracking = true;
      }, { passive: true });
      stage.addEventListener('touchend', function (e) {
        if (!tracking) return;
        tracking = false;
        var dx = e.changedTouches[0].clientX - sx;
        var dy = e.changedTouches[0].clientY - sy;
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.4) show(current + (dx < 0 ? 1 : -1));
      }, { passive: true });
    }

    // Pause the film whenever it scrolls out of view — it must never keep
    // decoding behind three screens of copy.
    if (video && 'IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (!en.isIntersecting) video.pause();
          else if (slides[current].hasAttribute('data-belt-video-slide')) playVideo();
        });
      }, { threshold: 0.25 }).observe(gal);
    }

    // Let the bundle picker drive the gallery: pick a leather or a buckle and
    // the photo of that exact combination comes forward.
    window.WADESShowImage = function (imageId) {
      for (var i = 0; i < slides.length; i++) {
        if (slides[i].getAttribute('data-belt-image-id') !== String(imageId)) continue;
        show(i);
        // Bring the photo itself into view — the point of picking a colour is
        // to see it, and on a phone the picker sits well below the gallery.
        var stage = qs('.beltg__stage', gal);
        if (stage) {
          var top = stage.getBoundingClientRect().top + window.pageYOffset;
          var head = parseInt(getComputedStyle(document.documentElement)
            .getPropertyValue('--header-h'), 10) || 0;
          if (stage.getBoundingClientRect().top < head) {
            window.scrollTo({ top: Math.max(0, top - head - 12), behavior: reduced ? 'auto' : 'smooth' });
          }
        }
        return true;
      }
      return false;
    };

    show(current);
  })();

  /* ============================================================
     2. BUNDLE TIERS + COLOUR PICKS + ADD TO CART
     ============================================================ */
  (function () {
    var root = qs('[data-belt-root]');
    if (!root) return;

    var product = null;
    try { product = JSON.parse(qs('[data-belt-product-json]', root).textContent); } catch (e) {}
    if (!product || !product.variants) return;

    var colorPos = parseInt(root.getAttribute('data-color-pos'), 10) || 0;
    var bucklePos = parseInt(root.getAttribute('data-buckle-pos'), 10) || 0;
    var boxes = qsa('[data-belt-tier]', root);
    var totalEl = qs('[data-belt-total]', root);
    var saveEl = qs('[data-belt-save]', root);
    var stickyPrice = qs('[data-belt-sticky-price]');
    var stickyLabel = qs('[data-belt-sticky-label]');
    var selected = 1;

    // A belt is chosen on two axes. Match on whichever of them the product
    // actually has, so a one-option catalogue keeps working unchanged.
    function variantFor(color, buckle) {
      var ck = colorPos ? 'option' + colorPos : null;
      var bk = bucklePos ? 'option' + bucklePos : null;
      if (!ck && !bk) return product.variants[0];

      var partial = null;
      for (var i = 0; i < product.variants.length; i++) {
        var v = product.variants[i];
        var okC = !ck || v[ck] === color;
        var okB = !bk || v[bk] === buckle;
        if (okC && okB) return v;
        if (okC && !partial) partial = v;   // right leather, buckle sold out
      }
      return partial || product.variants[0];
    }

    // Is this combination something the shop can actually sell?
    function availableWith(kind, value, other) {
      var ck = colorPos ? 'option' + colorPos : null;
      var bk = bucklePos ? 'option' + bucklePos : null;
      for (var i = 0; i < product.variants.length; i++) {
        var v = product.variants[i];
        if (v.available === false) continue;
        if (kind === 'color') {
          if (ck && v[ck] !== value) continue;
          if (bk && other && v[bk] !== other) continue;
        } else {
          if (bk && v[bk] !== value) continue;
          if (ck && other && v[ck] !== other) continue;
        }
        return true;
      }
      return false;
    }

    function currentBox() {
      return boxes.filter(function (b) { return parseInt(b.getAttribute('data-belt-tier'), 10) === selected; })[0];
    }

    function selectTier(n) {
      selected = n;
      boxes.forEach(function (b) {
        var mine = parseInt(b.getAttribute('data-belt-tier'), 10) === n;
        b.classList.toggle('is-selected', mine);
        var head = qs('[data-belt-tier-head]', b);
        if (head) head.setAttribute('aria-pressed', mine ? 'true' : 'false');
      });
      var box = currentBox();
      if (!box) return;
      var total = parseInt(box.getAttribute('data-total'), 10) || 0;
      var save = parseInt(box.getAttribute('data-save'), 10) || 0;
      if (totalEl) totalEl.textContent = money(total);
      if (stickyPrice) stickyPrice.textContent = money(total);
      if (stickyLabel) {
        var nm = qs('.beltt__name', box);
        if (nm) stickyLabel.textContent = nm.textContent.trim();
      }
      if (saveEl) {
        if (save > 0) { saveEl.textContent = SAVE_FMT.replace('%s', money(save)); saveEl.hidden = false; }
        else { saveEl.hidden = true; }
      }
    }

    root.addEventListener('click', function (e) {
      var head = e.target.closest('[data-belt-tier-head]');
      if (head) {
        var box = head.closest('[data-belt-tier]');
        selectTier(parseInt(box.getAttribute('data-belt-tier'), 10));
        return;
      }

      var sw = e.target.closest('[data-belt-swatch]');
      if (sw) {
        var pick = sw.closest('[data-belt-pick]');
        if (!pick) return;
        var kind = sw.getAttribute('data-belt-swatch');
        pick.setAttribute(kind === 'color' ? 'data-pick-color' : 'data-pick-buckle',
          sw.getAttribute('data-belt-value'));
        paintPick(pick, true);
      }
    });

    // Redraw one belt's picker: selected states, what is still buyable on the
    // other axis, the written summary, and the photo in the gallery.
    function paintPick(pick, showImage) {
      var color = pick.getAttribute('data-pick-color');
      var buckle = pick.getAttribute('data-pick-buckle');

      qsa('[data-belt-swatch]', pick).forEach(function (b) {
        var kind = b.getAttribute('data-belt-swatch');
        var val = b.getAttribute('data-belt-value');
        var on = val === (kind === 'color' ? color : buckle);
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
        var can = availableWith(kind, val, kind === 'color' ? buckle : color);
        b.classList.toggle('is-oos', !can);
        b.disabled = false;                       // still selectable, just marked
      });

      var label = qs('[data-belt-pick-label]', pick);
      if (label) label.textContent = buckle ? color + ' · ' + buckle : color;

      if (showImage) {
        var variant = variantFor(color, buckle);
        if (variant && variant.image_id && window.WADESShowImage) {
          window.WADESShowImage(variant.image_id);
        }
      }
    }

    boxes.forEach(function (b) {
      if (b.classList.contains('is-selected')) selected = parseInt(b.getAttribute('data-belt-tier'), 10) || 1;
    });
    selectTier(selected);

    // Draw every picker once, without touching the gallery: opening the page
    // should not move the photo off the shot the merchant chose to lead with.
    qsa('[data-belt-pick]', root).forEach(function (pick) { paintPick(pick, false); });

    // ---- Sticky bar: send them to the packages, don't buy behind their back ----
    // The bundle they land on decides quantity and colours, so adding straight
    // from a bar that shows neither would put something in the cart they never
    // chose. Scroll to the picker and flash it instead.
    document.addEventListener('click', function (e) {
      var jump = e.target.closest('[data-belt-jump]');
      if (!jump) return;
      e.preventDefault();
      var picker = qs('[data-belt-picker]');
      if (!picker) return;

      var head = parseInt(getComputedStyle(document.documentElement)
        .getPropertyValue('--header-h'), 10) || 0;
      var top = picker.getBoundingClientRect().top + window.pageYOffset - head - 18;
      window.scrollTo({ top: Math.max(0, top), behavior: reduced ? 'auto' : 'smooth' });

      picker.classList.remove('is-flash');
      void picker.offsetWidth;                       // restart the animation
      picker.classList.add('is-flash');
      setTimeout(function () { picker.classList.remove('is-flash'); }, 1400);
    });

    // ---- Add to cart (the inline button) ----
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-belt-add]');
      if (!btn) return;
      e.preventDefault();

      var box = currentBox();
      if (!box) return;
      var picks = qsa('[data-belt-pick]', box);
      var items = [];

      picks.forEach(function (pick) {
        var variant = variantFor(pick.getAttribute('data-pick-color'),
                                 pick.getAttribute('data-pick-buckle'));
        if (!variant) return;
        // Same colour twice becomes quantity 2 on one line rather than two
        // identical lines, which reads much better in the drawer.
        var existing = items.filter(function (it) { return it.id === variant.id; })[0];
        if (existing) existing.quantity += 1;
        else items.push({ id: variant.id, quantity: 1 });
      });

      if (!items.length && product.variants[0]) items.push({ id: product.variants[0].id, quantity: selected });
      if (!items.length) return;

      btn.classList.add('is-shine');
      setTimeout(function () { btn.classList.remove('is-shine'); }, 1200);

      if (window.WADESAddToCart) window.WADESAddToCart(items, btn);
      else window.location.href = '/cart';
    });
  })();

  /* ============================================================
     3. STICKY BUY BAR
     ============================================================ */
  (function () {
    var sticky = qs('[data-belt-sticky]');
    // Anchored to the whole bundle picker, not to the add-to-cart button: the
    // bar shows whenever the picker is off screen, above it as much as below,
    // and its button leads back to the picker rather than buying blind.
    var anchor = qs('[data-belt-picker]') || qs('.beltb .belt-btn[data-belt-add]');
    if (!sticky || !anchor) return;

    // Measured on scroll rather than via IntersectionObserver. The bundle rows
    // expand and collapse, which moves the element mid-observation; the observer
    // could end up never re-firing on the way back down, leaving the bar hidden
    // for the whole rest of the page. A rect check cannot drift.
    var ticking = false;
    function update() {
      ticking = false;
      // Hidden only while some part of the picker is actually on screen.
      var r = anchor.getBoundingClientRect();
      var inView = r.bottom > 0 && r.top < window.innerHeight;
      sticky.classList.toggle('is-visible', !inView);
      sticky.setAttribute('aria-hidden', inView ? 'true' : 'false');
    }
    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    // The tier bodies animate open/closed, so re-measure once they settle.
    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-belt-tier-head]') || e.target.closest('[data-belt-acc]')) {
        setTimeout(update, 420);
      }
    });
    update();
  })();

  /* ============================================================
     4. ACCORDIONS (grid-rows transition, so no fixed heights)
     ============================================================ */
  document.addEventListener('click', function (e) {
    var t = e.target.closest('[data-belt-acc]');
    if (!t) return;
    var open = t.getAttribute('aria-expanded') === 'true';
    t.setAttribute('aria-expanded', open ? 'false' : 'true');
    var body = t.nextElementSibling;
    if (!body) return;
    body.classList.toggle('is-open', !open);

    // Run the star chart every time the panel opens: reset to zero on close so
    // it draws itself again rather than sitting there already finished.
    qsa('[data-belt-acc-bar]', body).forEach(function (bar) {
      var b = bar.querySelector('b');
      if (!b) return;
      if (open) { b.style.width = '0%'; return; }
      var pct = bar.getAttribute('data-pct') || 0;
      if (reduced) { b.style.width = pct + '%'; return; }
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () { b.style.width = pct + '%'; });
      });
    });
  });

  /* ============================================================
     4b. REVIEWS INSIDE THE BUY-BOX ACCORDION
     The accordion shows the real reviews rather than bouncing the visitor
     down the page. They are copied from the reviews section so the merchant
     still edits them in exactly one place.
     ============================================================ */
  (function () {
    // The star breakdown, copied from the reviews section. It is filled in
    // when the accordion opens rather than on load, because a collapsed
    // accordion has no height for an observer to see.
    var barSlot = qs('[data-belt-acc-bars]');
    var barSrc = qs('.beltrev__bars');
    if (barSlot && barSrc) {
      var barsCopy = barSrc.cloneNode(true);
      qsa('[data-belt-bar]', barsCopy).forEach(function (bar) {
        bar.removeAttribute('data-belt-bar');       // the section below owns that one
        bar.setAttribute('data-belt-acc-bar', '');
      });
      barSlot.appendChild(barsCopy);
    }

    var slot = qs('[data-belt-acc-reviews]');
    if (!slot) return;
    var source = qsa('[data-belt-review]');
    if (!source.length) return;

    source.slice(0, 3).forEach(function (card) {
      var copy = card.cloneNode(true);
      copy.removeAttribute('data-belt-review');   // keep it out of the filter/pager
      copy.removeAttribute('id');
      copy.classList.remove('is-extra');
      // The lightbox is wired to the section below; a photo here is decoration.
      qsa('[data-belt-review-photo]', copy).forEach(function (img) {
        img.removeAttribute('data-belt-review-photo');
      });
      slot.appendChild(copy);
    });
  })();

  /* ============================================================
     5. SMOOTH SCROLL TO REVIEWS
     ============================================================ */
  document.addEventListener('click', function (e) {
    var link = e.target.closest('[data-belt-scroll-reviews]');
    if (!link) return;
    var target = qs('#omdomen');
    if (!target) return;
    e.preventDefault();
    target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
  });

  /* ============================================================
     6. REVIEWS — rating bars, filter chips, show more, lightbox
     ============================================================ */
  (function () {
    var sec = qs('[data-belt-reviews]');
    if (!sec) return;

    // Animate the distribution bars once they are on screen.
    var bars = qsa('[data-belt-bar]', sec);
    function fillBars() {
      bars.forEach(function (bar) {
        var b = bar.querySelector('b');
        if (b) b.style.width = (bar.getAttribute('data-pct') || 0) + '%';
      });
    }
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        if (entries[0].isIntersecting) { fillBars(); io.disconnect(); }
      }, { threshold: 0.2 });
      io.observe(sec);
    } else { fillBars(); }

    var list = qs('[data-belt-review-list]', sec);
    var moreBtn = qs('[data-belt-review-more]', sec);

    // Liquid renders everything past the visible limit with .is-extra; this just
    // drops that class. Nothing is fetched, so "show more" is instant.
    function revealAll() {
      qsa('.beltrv.is-extra', sec).forEach(function (c) { c.classList.remove('is-extra'); });
      if (moreBtn && moreBtn.parentElement) moreBtn.parentElement.hidden = true;
    }

    if (moreBtn) moreBtn.addEventListener('click', revealAll);

    sec.addEventListener('click', function (e) {
      var chip = e.target.closest('[data-belt-filter]');
      if (chip) {
        var want = chip.getAttribute('data-belt-filter');
        qsa('[data-belt-filter]', sec).forEach(function (c) { c.classList.toggle('is-active', c === chip); });
        // Filtering only makes sense against the whole set, so open it first.
        revealAll();
        qsa('[data-belt-review]', list).forEach(function (card) {
          var stars = card.getAttribute('data-stars');
          var hasPhoto = card.hasAttribute('data-has-photo');
          var show = want === 'all'
            || (want === 'photo' && hasPhoto)
            || (want === stars);
          card.classList.toggle('is-filtered-out', !show);
        });
        return;
      }

      var photo = e.target.closest('[data-belt-review-photo]');
      if (photo) openLb(photo.getAttribute('src'));
    });

    // ---- Lightbox ----
    var lb = qs('[data-belt-lb]');
    var lbImg = lb ? qs('img', lb) : null;
    function openLb(src) {
      if (!lb || !lbImg || !src) return;
      lbImg.src = src;
      lb.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    }
    function closeLb() {
      if (!lb) return;
      lb.classList.remove('is-open');
      document.body.style.overflow = '';
    }
    if (lb) {
      lb.addEventListener('click', function (e) {
        if (e.target.closest('[data-belt-lb-close]') || e.target.classList.contains('beltrev__lb-bg')) closeLb();
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && lb.classList.contains('is-open')) closeLb();
      });
    }

  })();

  /* ============================================================
     7. FIT COMPARISON — two belts, one slider
     ============================================================ */
  (function () {
    var root = qs('[data-belt-vs-root]');
    if (!root) return;

    var range = qs('[data-vs]', root);
    if (!range) return;

    var cmEl = qs('[data-vs-cm]', root);
    var inEl = qs('[data-vs-in]', root);
    var holesEl = qs('[data-vs-holes]', root);
    var themPin = qs('[data-vs-them-pin]', root);
    var usPin = qs('[data-vs-us-pin]', root);
    var ghost = qs('[data-vs-ghost]', root);
    var errBar = qs('[data-vs-err]', root);
    var themV = qs('[data-vs-them-verdict]', root);
    var usV = qs('[data-vs-us-verdict]', root);

    var min = parseFloat(root.getAttribute('data-min')) || 78;
    var max = parseFloat(root.getAttribute('data-max')) || 108;
    var holeFirst = parseFloat(root.getAttribute('data-hole-first')) || 85;
    var holeGap = (parseFloat(root.getAttribute('data-hole-gap')) || 25) / 10; // mm -> cm
    var holeCount = parseInt(root.getAttribute('data-holes'), 10) || 5;
    var toothCm = (parseFloat(root.getAttribute('data-tooth')) || 3) / 10;
    var posBase = parseFloat(root.getAttribute('data-pos-base')) || 60;
    var steps = parseInt(root.getAttribute('data-steps'), 10) || 233;

    var L = {
      tight: root.getAttribute('data-lbl-tight') || '',
      loose: root.getAttribute('data-lbl-loose') || '',
      nofit: root.getAttribute('data-lbl-nofit') || '',
      exact: root.getAttribute('data-lbl-exact') || '',
      hole: root.getAttribute('data-lbl-hole') || '',
      step: root.getAttribute('data-lbl-step') || ''
    };

    // Where each of the five holes sits, in cm.
    var holes = [];
    for (var h = 0; h < holeCount; h++) holes.push(holeFirst + h * holeGap);

    function pct(cm) { return ((cm - min) / (max - min)) * 100; }

    // Paint the holes once — they never move, which is the entire point.
    if (holesEl && !holesEl.childElementCount) {
      holes.forEach(function (cm, i) {
        var d = document.createElement('span');
        d.className = 'beltvs__hole';
        d.style.left = pct(cm) + '%';
        d.setAttribute('data-i', i + 1);
        holesEl.appendChild(d);
      });
    }
    var holeEls = qsa('.beltvs__hole', root);

    function fmt(n) {
      // Swedish decimal comma, one place, no trailing ",0".
      var s = (Math.round(n * 10) / 10).toFixed(1).replace('.', ',');
      return s.replace(/,0$/, '');
    }

    function render() {
      var cm = parseFloat(range.value);
      var p = pct(cm);

      if (cmEl) cmEl.textContent = Math.round(cm);
      if (inEl) inEl.textContent = Math.round(cm / 2.54) + '"';

      // ---- Ours: lands exactly on the measurement ----
      if (usPin) usPin.style.left = p + '%';
      if (usV) {
        // Counted along the strap, so the figure matches the 233 positions the
        // comparison table quotes rather than the width of this slider.
        var stepIdx = Math.floor((cm - posBase) / toothCm) + 1;
        if (stepIdx < 1) stepIdx = 1;
        if (stepIdx > steps) stepIdx = steps;
        usV.textContent = L.step + ' ' + stepIdx + ' · ' + L.exact;
      }

      // ---- Theirs: can only snap to the nearest fixed hole ----
      var best = 0, bestD = Infinity;
      holes.forEach(function (hc, i) {
        var d = Math.abs(hc - cm);
        if (d < bestD) { bestD = d; best = i; }
      });
      var snapped = holes[best];
      // Past the end holes the belt still buckles, it is just increasingly
      // wrong — which is the honest and the more persuasive picture. Only well
      // beyond that does it stop reaching at all.
      var reach = holeGap * 1.6;
      var outOfRange = cm < holes[0] - reach || cm > holes[holes.length - 1] + reach;

      if (themPin) themPin.style.left = pct(snapped) + '%';
      holeEls.forEach(function (el, i) { el.classList.toggle('is-on', i === best && !outOfRange); });

      // The red bar spans the gap between where you are and where it locks.
      // It is hidden only on the five waists that happen to land on a hole.
      if (errBar) {
        if (bestD < 0.05) {
          errBar.style.opacity = '0';
        } else {
          var a = Math.min(pct(cm), pct(snapped));
          var b = Math.max(pct(cm), pct(snapped));
          errBar.style.opacity = '1';
          errBar.style.left = a + '%';
          errBar.style.width = (b - a) + '%';
        }
      }
      if (ghost) ghost.style.left = p + '%';

      if (themV) {
        if (outOfRange) {
          themV.textContent = L.nofit;
        } else {
          var dir = snapped < cm ? L.tight : L.loose;
          themV.textContent = bestD < 0.05
            ? L.hole + ' ' + (best + 1)
            : L.hole + ' ' + (best + 1) + ' · ' + fmt(bestD) + ' cm ' + dir;
        }
        themV.classList.toggle('is-nofit', outOfRange);
      }
    }

    range.addEventListener('input', render);
    range.addEventListener('change', render);

    // Run it once when it scrolls in, so the snapping is seen rather than
    // sitting there looking like a static diagram.
    var sec = root;
    if ('IntersectionObserver' in window && !reduced) {
      var seen = false;
      new IntersectionObserver(function (entries) {
        if (!entries[0].isIntersecting || seen) return;
        seen = true;
        var target = parseFloat(range.value);
        var from = Math.max(min, target - 11);
        range.value = from;
        render();
        var t0 = null;
        function ease(ts) {
          if (!t0) t0 = ts;
          var k = Math.min(1, (ts - t0) / 1100);
          var e = 1 - Math.pow(1 - k, 3);
          range.value = Math.round(from + (target - from) * e);
          render();
          if (k < 1) window.requestAnimationFrame(ease);
        }
        window.requestAnimationFrame(ease);
      }, { threshold: 0.4 }).observe(sec);
    }

    render();
  })();

  /* ============================================================
     8. AMBIENT SECTION VIDEO (the full-width film band)
     ============================================================ */
  (function () {
    var band = qs('[data-belt-film]');
    if (!band) return;
    var vid = qs('video', band);
    if (!vid || !('IntersectionObserver' in window)) return;
    new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (en.isIntersecting) {
          if (vid.preload === 'none') { vid.preload = 'auto'; vid.load(); }
          var p = vid.play();
          if (p && p.catch) p.catch(function () {});
        } else { vid.pause(); }
      });
    }, { threshold: 0.3 }).observe(band);
  })();
})();
